import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, prefersReducedMotion } from '../utils/motion';

type RevealProps = {
  as?: keyof JSX.IntrinsicElements;
  children: React.ReactNode;
  delayMs?: number;
  className?: string;
  style?: React.CSSProperties;
};

const Wrapper = styled.div<{ $revealed: boolean; $delay: number }>`
  opacity: ${(p) => (p.$revealed ? 1 : 0)};
  transform: translateY(${(p) => (p.$revealed ? '0px' : '16px')});
  transition: opacity ${motion.duration.md}ms ${motion.easing.entrance},
    transform ${motion.duration.md}ms ${motion.easing.entrance};
  transition-delay: ${(p) => p.$delay}ms;
  will-change: opacity, transform;
`;

const Reveal: React.FC<RevealProps> = ({ as, children, delayMs = 0, className, style }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(prefersReducedMotion());

  useEffect(() => {
    if (prefersReducedMotion()) {
      setRevealed(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // @ts-ignore styled-components supports "as" polymorphic prop
  return (
    <Wrapper ref={ref} as={as as any} $revealed={revealed} $delay={delayMs} className={className} style={style}>
      {children}
    </Wrapper>
  );
};

export default Reveal;
